import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Users, TrendingUp, BookOpen, BarChart3, Award, Activity } from 'lucide-react';
import { Progress } from '@/components/ui/progress';

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className="p-2 rounded-lg bg-gray-100"><Icon className="w-4 h-4 text-gray-500" /></div>
        <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">{label}</span>
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/analytics').then(res => setData(res.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#F5C518] border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return <p className="text-gray-400 text-center py-20">Failed to load analytics</p>;

  const maxCompletions = Math.max(...(data.heat_map?.map(r => r.completions) || [1]), 1);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-analytics">
      <h2 className="text-xl font-bold text-gray-900">Dashboard</h2>

      {/* Stats grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Total Users" value={data.total_users} sub={`+${data.new_users_7d} this week`} />
        <StatCard icon={TrendingUp} label="New (30d)" value={data.new_users_30d} />
        <StatCard icon={Activity} label="Active (7d)" value={data.active_users_7d} />
        <StatCard icon={Award} label="Completions" value={data.total_completions} />
      </div>

      {/* Chapter Engagement */}
      <div className="bg-white border border-gray-200 rounded-xl p-6" data-testid="chapter-engagement">
        <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-[#D4A910]" />Chapter Engagement</h3>
        <div className="space-y-3">
          {data.chapter_engagement?.map(ch => (
            <div key={ch.chapter_number} className="flex items-center gap-3">
              <span className="w-6 h-6 rounded bg-[#F5C518]/10 flex items-center justify-center text-xs font-bold text-[#D4A910] shrink-0">{ch.chapter_number}</span>
              <span className="text-xs text-gray-500 w-48 truncate shrink-0">{ch.chapter_name}</span>
              <div className="flex-1 gold-progress"><Progress value={ch.avg_completion_pct} className="h-2 bg-gray-100" /></div>
              <span className="text-xs font-medium text-[#D4A910] w-12 text-right">{ch.avg_completion_pct}%</span>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Popular Resources */}
        <div className="bg-white border border-gray-200 rounded-xl p-6" data-testid="popular-resources">
          <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2"><Award className="w-4 h-4 text-[#D4A910]" />Most Popular Resources</h3>
          {data.popular_resources?.length > 0 ? (
            <div className="space-y-2">
              {data.popular_resources.map((r, i) => (
                <div key={r.resource_id} className="flex items-center gap-3 py-2">
                  <span className="w-5 h-5 rounded bg-[#F5C518]/10 flex items-center justify-center text-[10px] font-bold text-[#D4A910]">{i + 1}</span>
                  <code className="text-[10px] font-mono text-gray-400 shrink-0">{r.url_path}</code>
                  <span className="text-xs text-gray-700 flex-1 truncate">{r.title}</span>
                  <span className="text-xs font-medium text-[#D4A910]">{r.completions}</span>
                </div>
              ))}
            </div>
          ) : <p className="text-xs text-gray-400">No completion data yet</p>}
        </div>

        {/* Completion Heat Map */}
        <div className="bg-white border border-gray-200 rounded-xl p-6" data-testid="completion-heatmap">
          <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2"><BookOpen className="w-4 h-4 text-[#D4A910]" />Completion Heat Map</h3>
          <div className="grid grid-cols-5 sm:grid-cols-7 gap-1.5">
            {data.heat_map?.map(r => {
              const intensity = Math.min(r.completions / maxCompletions, 1);
              return (
                <div key={r.resource_id} title={`${r.url_path}: ${r.title} (${r.completions} completions)`}
                  className="aspect-square rounded-md flex items-center justify-center text-[9px] font-mono font-bold cursor-default transition-transform hover:scale-110 border border-gray-100"
                  style={{ backgroundColor: `rgba(245, 197, 24, ${0.05 + intensity * 0.6})`, color: intensity > 0.3 ? '#111' : '#999' }}
                >{r.url_path}</div>
              );
            })}
          </div>
          <div className="flex items-center gap-2 mt-3">
            <span className="text-[10px] text-gray-400">Low</span>
            <div className="flex gap-0.5 flex-1">
              {[0.1, 0.2, 0.35, 0.5, 0.65].map((v, i) => (
                <div key={i} className="h-2 flex-1 rounded-sm" style={{ backgroundColor: `rgba(245, 197, 24, ${v})` }} />
              ))}
            </div>
            <span className="text-[10px] text-gray-400">High</span>
          </div>
        </div>
      </div>

      {/* Daily Registrations */}
      {data.daily_registrations?.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-6" data-testid="daily-registrations">
          <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-green-600" />Daily Registrations (30d)</h3>
          <div className="flex items-end gap-1 h-24">
            {data.daily_registrations.map(d => {
              const maxReg = Math.max(...data.daily_registrations.map(x => x.registrations), 1);
              const height = (d.registrations / maxReg) * 100;
              return (
                <div key={d.date} className="flex-1 flex flex-col items-center gap-1" title={`${d.date}: ${d.registrations} registrations`}>
                  <div className="w-full rounded-t bg-green-400 hover:bg-green-500 transition-colors" style={{ height: `${Math.max(height, 4)}%` }} />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
